import axios from 'axios';

const dataApiBase = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
const authApiBase = process.env.REACT_APP_AUTH_API_URL || dataApiBase;

// Data API: predictions, featured-criteria, picks, model-stats, etc. (same-origin on deploy, or staging backend when on staging)
const api = axios.create({
  baseURL: dataApiBase,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth API: login, register, /user, user prefs. When REACT_APP_AUTH_API_URL is set (staging), points at prod for auth.
const authApi = axios.create({
  baseURL: authApiBase,
  headers: {
    'Content-Type': 'application/json',
  },
});

function attachToken(config) {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}

api.interceptors.request.use(attachToken, (error) => Promise.reject(error));
authApi.interceptors.request.use(attachToken, (error) => Promise.reject(error));

// Sports API
export const getSports = async () => {
  try {
    const response = await api.get('/sports');
    return response.data;
  } catch (error) {
    console.error('Error fetching sports:', error);
    throw error;
  }
};

// Odds sportsbooks (Best Odds + individual books)
export const getSportsbooks = async () => {
  try {
    const response = await api.get('/odds/sportsbooks');
    return response.data;
  } catch (error) {
    console.error('Error fetching sportsbooks:', error);
    throw error;
  }
};

export const updateUserSportsbook = async (sportsbook) => {
  try {
    const response = await authApi.put('/user/sportsbook', { sportsbook });
    return response.data;
  } catch (error) {
    console.error('Error updating sportsbook preference:', error);
    throw error;
  }
};

export const updateEmailSubscription = async (subscribed) => {
  try {
    const response = await authApi.put('/user/email-subscription', { subscribed });
    return response.data;
  } catch (error) {
    console.error('Error updating email subscription:', error);
    throw error;
  }
};

export const updateEmailResultsSubscription = async (subscribed) => {
  try {
    const response = await authApi.put('/user/email-results-subscription', { subscribed });
    return response.data;
  } catch (error) {
    console.error('Error updating results email subscription:', error);
    throw error;
  }
};

// MLB Predictions API (sportsbook: 'best' or book key for odds display; model: 'v3' for testing)
export const getMlbTotalBasesPredictions = async (date = null, activeOnly = false, sportsbook = null, model = null) => {
  try {
    const params = {};
    if (date) params.date = date;
    if (activeOnly) params.activeOnly = true;
    if (sportsbook) params.sportsbook = sportsbook;
    if (model) params.model = model;
    const response = await api.get('/mlb/predictions/total-bases', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching MLB Total Bases predictions:', error);
    throw error;
  }
};

export const getMlbHrPredictions = async (date = null, activeOnly = false, sportsbook = null) => {
  try {
    const params = {};
    if (date) params.date = date;
    if (activeOnly) params.activeOnly = true;
    if (sportsbook) params.sportsbook = sportsbook;
    const response = await api.get('/mlb/predictions/hr', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching MLB HR predictions:', error);
    throw error;
  }
};

export const getMlbHitPredictions = async (date = null, activeOnly = false, sportsbook = null) => {
  try {
    const params = {};
    if (date) params.date = date;
    if (activeOnly) params.activeOnly = true;
    if (sportsbook) params.sportsbook = sportsbook;
    const response = await api.get('/mlb/predictions/hit', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching MLB Hit predictions:', error);
    throw error;
  }
};

export const getMlbTwoHitsPredictions = async (date = null, activeOnly = false, sportsbook = null) => {
  try {
    const params = {};
    if (date) params.date = date;
    if (activeOnly) params.activeOnly = true;
    if (sportsbook) params.sportsbook = sportsbook;
    const response = await api.get('/mlb/predictions/two-hits', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching MLB 2+ Hits predictions:', error);
    throw error;
  }
};

// Picks ledger (POTD + Parlay history)
export const getPicksLedger = async () => {
  const response = await api.get('/model-stats');
  return response.data;
};

/** @deprecated Use getPicksLedger */
export const getModelStats = getPicksLedger;

export const getAdminUsers = async () => {
  try {
    const response = await api.get('/admin/users');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Admin + local-only: debug model features for a player on a date (requires ENABLE_DEBUG_PLAYER_FEATURES on backend). */
export const getAdminDebugPlayerFeatures = async (dateStr, playerId, model = 'all') => {
  const response = await api.get('/admin/debug-player-features', {
    params: { date: dateStr, player_id: playerId, model },
  });
  return response.data;
};

/** Admin + local-only: get ordered feature names for a model (needs date for structure). */
export const getAdminDebugModelFeatureNames = async (dateStr, model) => {
  const response = await api.get('/admin/debug-model-feature-names', {
    params: { date: dateStr, model },
  });
  return response.data;
};

/** Admin + local-only: run model prediction with optional feature overrides. */
export const postAdminDebugModelPredict = async (body) => {
  const response = await api.post('/admin/debug-model-predict', body);
  return response.data;
};

/** Admin + local-only: list MLB teams for team-view dropdown. */
export const getAdminDebugTeams = async () => {
  const response = await api.get('/admin/debug-teams');
  return response.data;
};

/** Admin + local-only: get features and predictions for all players on a team. */
export const getAdminDebugTeamFeatures = async (dateStr, teamId, model = 'all') => {
  const response = await api.get('/admin/debug-team-features', {
    params: { date: dateStr, team_id: teamId, model },
  });
  return response.data;
};

/** Admin: invoke a pipeline Lambda (run-job). Requires ENABLE_LAMBDA_RUN_JOBS on backend. */
export const postAdminRunJob = async (job, payload = {}) => {
  const response = await api.post('/admin/run-job', { job, payload });
  return response.data;
};

/**
 * Admin: send a manual Discord test post to test/public/custom webhook target.
 *
 * @param {object} body — may include use_production_template, notification_type
 *   (all template kinds including parlay_hit), date, game_id (parlay row),
 *   data, raw_payload, image_data_url, etc.
 */
export const postAdminDiscordTest = async (body) => {
  const response = await api.post('/admin/discord-test', body);
  return response.data;
};

/**
 * Admin: preview production Discord payload before sending.
 *
 * @param {object} body — notification_type, date, optional data, game_id,
 *   include_image, image_filename.
 */
export const postAdminDiscordPreview = async (body) => {
  const response = await api.post('/admin/discord-preview', body);
  return response.data;
};

/** Admin: get the current social poster mode config (off | testing | production). */
export const getAdminSocialPosterConfig = async () => {
  const response = await api.get('/admin/social-poster-config');
  return response.data;
};

/** Admin: update the social poster mode.
 * @param {{ mode: 'off' | 'testing' | 'production' }} body
 */
export const putAdminSocialPosterConfig = async (body) => {
  const response = await api.put('/admin/social-poster-config', body);
  return response.data;
};

/**
 * Admin: invoke the social poster Lambda in test_mode for a pick type/date.
 * Sends pick card image + all platform content previews to the test Discord
 * webhook only — no real posts to X, Instagram, or TikTok.
 *
 * @param {{
 *   notification_type: 'picks_of_the_day' | 'parlay_of_the_day' | 'parlay_hit',
 *   date: string,
 *   game_id?: string,
 * }} body
 * @returns {Promise<{ ok: boolean, pick_type: string, date: string, results: object, game_id?: string }>}
 */
export const postAdminSocialPosterTest = async (body) => {
  const response = await api.post('/admin/social-poster-test', body);
  return response.data;
};

/**
 * Admin: email picks or results subscribers using the same payload as Discord (Social Media Mgmt).
 *
 * @param {{
 *   subscription: 'picks' | 'results',
 *   audience?: 'subscribers' | 'admins',
 *   email_format?: 'discord' | 'picks_social_with_insights',
 *   date?: string,
 *   raw_payload?: object,
 *   use_production_template?: boolean,
 *   notification_type?: string,
 *   data?: object,
 *   image_data_url?: string,
 *   image_filename?: string,
 * }} body
 * @returns {Promise<{ ok: boolean, emails_sent: number, subject: string, subscription: string, audience: string }>}
 */
export const postAdminSocialSubscriberEmail = async (body) => {
  const response = await api.post('/admin/social-subscriber-email', body);
  return response.data;
};

/** Admin: preview rendered welcome email template. Optional first_name for sample. */
export const getEmailTemplatePreview = async (first_name = 'Sample') => {
  const response = await api.get('/admin/email-template-preview', {
    params: { first_name },
  });
  return response.data;
};

// GenAI Picks of the Day (home page). Returns { date, picks_of_the_day, summary_blurb? } or 404.
export const getPicksOfTheDay = async (dateStr) => {
  const response = await api.get('/picks-of-the-day', { params: { date: dateStr } });
  return response.data;
};

// GenAI Game Picks (per-game favorable picks). Returns { date, games } or 404.
export const getGamePicks = async (dateStr) => {
  const response = await api.get('/game-picks', { params: { date: dateStr } });
  return response.data;
};

// GenAI Parlay(s) of the Day. Returns { date, parlays: [...] }; 404 → empty parlays.
export const getParlayOfTheDay = async (dateStr) => {
  try {
    const response = await api.get('/parlay-of-the-day', { params: { date: dateStr } });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return { date: dateStr, parlays: [] };
    }
    throw error;
  }
};

// Watched/tracked picks (logged-in users)
export const createWatchedPick = async (body) => {
  const response = await api.post('/watched-picks', body);
  return response.data;
};

export const getWatchedPicks = async (params = {}) => {
  const response = await api.get('/watched-picks', { params });
  return response.data;
};

export const deleteWatchedPick = async (id) => {
  const response = await api.delete(`/watched-picks/${id}`);
  return response.data;
};

/** Remove all tracked picks; pending simulated stakes are refunded. */
export const clearAllWatchedPicks = async () => {
  const response = await api.delete('/watched-picks');
  return response.data;
};

export const getSimulatedBalance = async () => {
  const response = await api.get('/user/simulated-balance');
  return response.data;
};

export const resetSimulatedBalance = async () => {
  const response = await api.post('/user/simulated-balance/reset');
  return response.data;
};

export const requestPasswordReset = async (email) => {
  const response = await authApi.post('/password-reset/request', { email });
  return response.data;
};

export const confirmPasswordReset = async (token, newPassword) => {
  const response = await authApi.post('/password-reset/confirm', { token, newPassword });
  return response.data;
};

export { authApi };
export default api; 